import { supabase } from "@/integrations/supabase/client";

/** Whether the user holds the admin role. */
export async function fetchIsAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();

  return !error && !!data;
}

/** The organization a (non-admin) user belongs to, if any. */
export async function fetchUserOrganizationId(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from("organization_members")
    .select("organization_id")
    .eq("user_id", userId)
    .maybeSingle();

  return data?.organization_id ?? null;
}

/** The client record tied to the user's organization, if any. */
export async function fetchUserClientId(userId: string): Promise<string | null> {
  const organizationId = await fetchUserOrganizationId(userId);
  if (!organizationId) return null;

  const { data } = await supabase
    .from("clients")
    .select("id")
    .eq("organization_id", organizationId)
    .maybeSingle();

  return data?.id ?? null;
}

/**
 * Single source of truth for where a signed-in user should land:
 * admins go to the dashboard, clients to their own portal.
 */
export async function resolveUserLandingPath(userId: string): Promise<string> {
  if (await fetchIsAdmin(userId)) return "/dashboard";

  const clientId = await fetchUserClientId(userId);
  return clientId ? `/clients/${clientId}` : "/dashboard";
}
